import { defineMessages, type IntlShape } from 'react-intl';
import type TrailActivityType from '@alltrails/trails/types/TrailActivityType';
import type TrailRouteType from '@alltrails/trails/types/TrailRouteType';
import type TrailSuitability from '@alltrails/trails/types/TrailSuitability';
import type TrailTraffic from '@alltrails/trails/types/TrailTraffic';
import getTrailActivityTypeLabel from '@alltrails/trails/utils/getTrailActivityTypeLabel';
import getTrailFeatureLabel from '@alltrails/trails/utils/getTrailFeatureLabel';
import getTrailRouteTypeLabel from '@alltrails/trails/utils/getTrailRouteTypeLabel';
import getTrailSuitabilityLabel from '@alltrails/trails/utils/getTrailSuitabilityLabel';
import type TrailFeature from '@alltrails/types/TrailFeature';
import { activityTypes } from '../hooks/filterConfigs/useActivityChipsConfig';
import type ExploreServiceFilters from '../types/ExploreServiceFilters';
import { difficulties } from './getValidDifficulties';
import { features } from './getValidFeatures';
import getVisitationLabel from './getVisitationLabel';

// Canonical order of applied-filter pill groups, mirroring the revamped All Filters modal's section order.
// `suitability` is split: dog/kid appear early (with the toggles), accessibility values appear later.
export const APPLIED_FILTER_GROUP_ORDER = [
  'difficulty',
  'length',
  'elevationGain',
  'highestPoint',
  'duration',
  'distanceAway',
  'suitabilityPrimary',
  'activity',
  'feature',
  'routeType',
  'accessibility',
  'trailTraffic',
  'minRating',
  'trailEngagement'
] as const;

export type AppliedFilterGroup = (typeof APPLIED_FILTER_GROUP_ORDER)[number];

// A pill's sort key: primary by group position, secondary by value position within the group.
export const getAppliedFilterSortOrder = (group: AppliedFilterGroup, valueIndex = 0): number =>
  APPLIED_FILTER_GROUP_ORDER.indexOf(group) * 100 + valueIndex;

export type AppliedFilter = {
  /** Stable id, e.g. "feature:lake". */
  id: string;
  label: string;
  /** The `filters` key this value lives under (both suitability groups map to "suitability"). */
  filterKey: Extract<keyof ExploreServiceFilters, 'difficulty' | 'suitability' | 'activity' | 'feature' | 'routeType' | 'trailTraffic'>;
  value: string;
  order: number;
};

const difficultyLabels = defineMessages({
  easy: { defaultMessage: 'Easy' },
  moderate: { defaultMessage: 'Moderate' },
  hard: { defaultMessage: 'Hard' },
  strenuous: { defaultMessage: 'Strenuous' }
});

type MultiSelectFilterGroup = {
  group: AppliedFilterGroup;
  filterKey: AppliedFilter['filterKey'];
  values: readonly string[];
  getLabel: (intl: IntlShape, value: string) => string;
};

// Value orders mirror the modal's chip order per filter; the two suitability groups pull from the same array.
const MULTI_SELECT_FILTER_GROUPS: MultiSelectFilterGroup[] = [
  {
    group: 'difficulty',
    filterKey: 'difficulty',
    values: difficulties,
    getLabel: (intl, value) => intl.formatMessage(difficultyLabels[value as keyof typeof difficultyLabels])
  },
  {
    group: 'suitabilityPrimary',
    filterKey: 'suitability',
    values: ['dogs', 'kids'],
    getLabel: (intl, value) => getTrailSuitabilityLabel(intl, value as TrailSuitability)
  },
  {
    group: 'activity',
    filterKey: 'activity',
    values: activityTypes,
    getLabel: (intl, value) => getTrailActivityTypeLabel(intl, value as TrailActivityType)
  },
  { group: 'feature', filterKey: 'feature', values: features, getLabel: (intl, value) => getTrailFeatureLabel(intl, value as TrailFeature) },
  {
    group: 'routeType',
    filterKey: 'routeType',
    values: ['O', 'L', 'P'],
    getLabel: (intl, value) => getTrailRouteTypeLabel(intl, value as TrailRouteType)
  },
  {
    group: 'accessibility',
    filterKey: 'suitability',
    values: ['ada', 'strollers', 'paved', 'partially-paved'],
    getLabel: (intl, value) => getTrailSuitabilityLabel(intl, value as TrailSuitability)
  },
  {
    group: 'trailTraffic',
    filterKey: 'trailTraffic',
    values: ['light', 'moderate', 'heavy'],
    getLabel: (intl, value) => getVisitationLabel(intl, value as TrailTraffic)
  }
];

/**
 * Flattens the multi-select filters into one removable entry per selected value, in canonical (modal section) order.
 */
export default function getAppliedMultiSelectFilters(filters: ExploreServiceFilters, intl: IntlShape): AppliedFilter[] {
  const appliedFilters: AppliedFilter[] = [];

  MULTI_SELECT_FILTER_GROUPS.forEach(({ group, filterKey, values, getLabel }) => {
    const applied = (filters[filterKey] as string[] | undefined) ?? [];
    values.forEach((value, valueIndex) => {
      if (applied.includes(value)) {
        appliedFilters.push({
          id: `${filterKey}:${value}`,
          label: getLabel(intl, value),
          filterKey,
          value,
          order: getAppliedFilterSortOrder(group, valueIndex)
        });
      }
    });
  });

  return appliedFilters;
}
